import { Injectable } from '@angular/core';
import { ProjectService } from '../project/project.service';
import { Subject, BehaviorSubject, Observable } from 'rxjs';
import { first, filter } from 'rxjs/operators';
import { ApiService } from '../../api/api.service';
import { ProjectModule } from './project-module.interface';
import { CircularDetectionService } from '../../utilities/circular-detection/circular-detection.service';
import { ConstantsService } from '../../utilities/constants/constants.service';
import { ApiLegacyService } from '../../api/legacy/api-legacy.service';
import { ConstantsRequirementType } from '../../utilities/constants/constants.interface';

/**
 * Module core service.
 *
 * Contains all the private logic for Module management.
 *
 * **Only ModuleService is allowed to access this Service.**
 */
@Injectable({
  providedIn: 'root'
})
export class ModuleCoreService {

  /** Module subject. */
  protected _modules: Subject<ProjectModule[]> = new BehaviorSubject<ProjectModule[]>(null);

  /** Module observable. */
  public module$ = this._modules.asObservable();

  private constantsRequirementTypes: ConstantsRequirementType[];

  /** Constructor. */
  constructor(
    protected apiService: ApiService,
    protected apiLegacyService: ApiLegacyService,
    protected circularDetectionService: CircularDetectionService,
    protected constantsService: ConstantsService,
    protected projectService: ProjectService,
  ) {
    this._trackProject();
  }


  /**
   * Get modules.
   *
   * @note Should actually only return when `active: true`, but this breaks the
   * project info page, since it needs the inactive modules for sorting/counting.
   *
   * @returns The current modules.
   */
  public get(): ProjectModule[] {
    let modules: ProjectModule[];
    this.module$.pipe(first()).subscribe(m => modules = m);
    return modules;
  }

  /**
   * Get one module.
   *
   * @param id The id of the requested module.
   * @returns The requested module.
   */
  public getModule(id: ProjectModule['_id']): ProjectModule {
    return this.get().find(m => m._id === id);
  }

  /**
   * Delete module.
   *
   * When deleting a module, a few things are done:
   *  - Call API to request a delete on backend.
   *  - Patch the local copy of the module.
   *  - Mark any child submodule as `active: false`.
   *  - Mark any child element as `active: false`.
   *
   * @note Each modules is patched individually. Should be the best solution because
   *  it handles the exact case of 'patch these N modules', however it triggers the
   *  observable a lot of times, one time for the current module, and every time for
   *  each adjecent sibling module. When performance will be an issue, just patch the
   *  parent (project) once.
   *
   * @param submodule The submodule to delete.
   */
  protected _delete(module: ProjectModule): Observable<boolean> {
    return new Observable(observer => {
      this.apiLegacyService.deleteModule(module).subscribe(() => {
        const project = this.projectService.get();
        const index = project.modules.findIndex(m => m._id === module._id);
        project.modules.forEach((mod, i) => {
          if (i === index) {
            for (const submodule of mod.submodules) {
              submodule.active = false;
              for (const element of submodule.elements) {
                element.active = false;
              }
            }
            this.projectService.patchModule(mod, { active: false });
          }
        });
        observer.next(true);
        observer.complete();
      });
    });
  }

  /**
   * Fix modules.
   *
   * Add requirement type to the module on root level, to make it easier for components to
   * check this value.
   *
   * However, because of the loading order / resolvers used by UiRouter, this check often fails
   * because constants aren't available yet. This method can be enabled after Router migration.
   */
  protected _fix(modules: ProjectModule[]): ProjectModule[] {
    this.constantsRequirementTypes = this.constantsRequirementTypes || this.constantsService.getRequirementTypes();
    for (const module of modules) {
      if (module.viewtab) {
        module.requirementType = this.constantsRequirementTypes.find(crt => crt.value === module.viewtab)._id;
      }
    }
    return modules;
  }

  /**
   * Patch module.
   *
   * @param module The module to patch.
   * @param values The updated values
   * @returns Observable with state.
   */
  protected _patchModule(module: ProjectModule, values: Partial<ProjectModule>, config?: any): Observable<boolean> {
    return new Observable(observer => {
      this.apiLegacyService.patchModule(module, values).subscribe(response => {
        this.projectService.patchModule(module, values);
        observer.next(true);
        observer.complete();
      });
    });
  }

  /**
   * Track project and set modules.
   *
   * @todo Enable _fix method after Router migration.
   */
  protected _trackProject(): void {
    this.projectService.project$.pipe(
      filter(project => project !== null)
    ).subscribe(project => {
      // Does not work because of UiRouter resolver order
      // this._modules.next(this._fix(project.modules));

      this._modules.next(project.modules);
    });
  }
}
